import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { chipClasses, filledInputClasses, inputBaseClasses, inputClasses, inputLabelClasses, Autocomplete as MuiAutocomplete, outlinedInputClasses, textFieldClasses } from '@mui/material';
import { styled } from '@mui/material/styles';
import TextField from './TextField';

/**
 * ERA² styled Autocomplete component that wraps MUI's Autocomplete with enhanced styling and layout consistency.
 *
 * This component provides:
 * - Consistent padding and spacing across different variants (outlined, filled, standard)
 * - Proper multi-select chip positioning and styling
 * - Alternative layout mode via 'alt' prop for different design systems
 * - Seamless integration with custom TextField component
 *
 * @example
 * // Basic single-select dropdown
 * <Autocomplete
 *   label="Country"
 *   placeholder="Select a country"
 *   options={['USA', 'Canada', 'Mexico']}
 *   value={selectedCountry}
 *   onChange={(event, value) => setSelectedCountry(value)}
 * />
 *
 * @example
 * // Multi-select with chips
 * <Autocomplete
 *   label="Skills"
 *   placeholder="Select skills"
 *   options={skillsList}
 *   multiple
 *   value={selectedSkills}
 *   onChange={(event, value) => setSelectedSkills(value)}
 * />
 *
 * @example
 * // Free text input with suggestions
 * <Autocomplete
 *   label="Custom Input"
 *   options={suggestions}
 *   freeSolo
 *   value={inputValue}
 *   onChange={(event, value) => setInputValue(value)}
 * />
 */

// Styled wrapper that handles complex layout variations across MUI input variants
const StyledAutocomplete = styled(MuiAutocomplete)(({ theme, alt }) => ({
  // TARGET: All TextField variants within the Autocomplete
  [`> .${textFieldClasses.root}`]: {
    // LABEL STYLING: Add padding when alt mode is enabled
    // Alt mode typically indicates alternative design system requirements
    [`> .${inputLabelClasses.root}`]: {
      paddingRight: alt ? '2rem' : 0, // Extra space for alt layout designs
    },

    // OUTLINED VARIANT: Most commonly used variant, requires precise padding control
    [`> .${outlinedInputClasses.root}`]: {
      // Reset default paddings to prevent spacing issues with chips and icons
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: 0,

      // SMALL SIZE: Compact input for dense layouts
      [`&.${inputBaseClasses.sizeSmall}`]: {
        [`>.${outlinedInputClasses.input}`]: {
          padding: '8px 8px 8px 14px', // Consistent with TextField small size
        },
      },

      // MEDIUM SIZE: Default size for most use cases
      [`&:not(.${inputBaseClasses.sizeSmall})`]: {
        [`>.${outlinedInputClasses.input}`]: {
          padding: '12px 8px 12px 14px', // Standard comfortable padding
        },
      },

      // MULTI-SELECT ACCOMMODATION: Adjust padding when chips are present
      // :has() selector detects presence of chip elements
      [`&:has(> .${chipClasses.root})`]: {
        paddingLeft: '4px', // Reduced left padding to align chips properly
      },

      // CHIP STYLING: Visual spacing for multi-select values
      [`> .${chipClasses.root}`]: {
        margin: '6px 0 6px 8px', // Consistent chip spacing and alignment
      },
    },

    // FILLED VARIANT: Alternative input style with background fill
    [`> .${filledInputClasses.root}`]: {
      // SMALL FILLED: Compact version of filled input
      [`&.${inputBaseClasses.sizeSmall}`]: {
        paddingTop: 0,
        paddingLeft: 0,

        [`>.${filledInputClasses.input}`]: {
          // Alt mode affects label positioning, requiring different padding
          padding: alt ? '21px 12px 4px' : '8px 12px',
        },
      },

      // MEDIUM FILLED: Standard filled input size
      [`&:not(.${inputBaseClasses.sizeSmall})`]: {
        paddingTop: 0,
        paddingLeft: 0,

        [`>.${filledInputClasses.input}`]: {
          // Alt mode requires more top padding for proper label clearance
          padding: alt ? '25px 12px 8px' : '12px',
        },
      },

      // FILLED + MULTI-SELECT: Complex layout when chips are present in filled variant
      [`&:has(> .${chipClasses.root})`]: {
        paddingTop: alt ? '12px' : 0, // Alt mode needs extra top padding
        paddingLeft: '2px',

        // Chip positioning adjustments for filled variant
        [`> .${chipClasses.root}`]: {
          marginTop: alt ? '10px' : '6px',
          marginBottom: alt ? '3px' : '6px',
        },

        // Input field adjustments when chips are present
        [`> .${filledInputClasses.input}`]: {
          paddingTop: alt ? '13px' : '8px',
        },
      },

      // Base chip styling for filled variant
      [`> .${chipClasses.root}`]: {
        margin: '6px 0 6px 8px',
      },
    },

    // STANDARD VARIANT: Underline-only input style (legacy/minimal design)
    [`> .${inputClasses.root}`]: {
      // SMALL STANDARD: Minimal styling adjustments
      [`&.${inputBaseClasses.sizeSmall}`]: {
        // Commented out - using MUI defaults
        // [`> .${inputClasses.input}`]: {
        //   padding: '8px 12px',
        // },
      },

      // MEDIUM STANDARD: Slight padding adjustment for alignment
      [`&:not(.${inputBaseClasses.sizeSmall})`]: {
        [`> .${inputClasses.input}`]: {
          padding: '4px 0', // Minimal padding for underline variant
        },
      },
    },
  },
}));

/**
 * Autocomplete component that provides enhanced dropdown/select functionality
 * with search, multi-select, and free-text input capabilities.
 */
const Autocomplete = forwardRef((props, ref) => {
  const {
    // STYLING PROPS: Control visual appearance
    color = 'primary', // Theme color for focus states and selection
    variant = 'outlined', // Input variant: outlined | filled | standard
    alt = false, // Alternative layout mode for different design systems
    size = 'medium', // Input size: small | medium

    // CONTENT PROPS: Core functionality
    label, // Field label text
    placeholder, // Placeholder text when no value selected
    options = [], // Array of selectable options
    multiple = false, // Enable multi-selection with chips
    freeSolo = false, // Allow custom text input beyond provided options

    // STATE & VALIDATION PROPS: Field state management
    helperText, // Explanatory text below the field
    required, // Mark field as required for forms
    disabled, // Disable all interactions
    error, // Error state styling
    hiddenLabel, // Hide the label (useful for dense layouts)

    // ADVANCED PROPS: Customization and behavior
    InputLabelProps, // Props passed to the label component

    ...otherProps // All other MUI Autocomplete props (onChange, value, etc.)
  } = props;

  return (
    <StyledAutocomplete
      // Pass alt as string attribute for styled-components CSS targeting
      alt={alt ? 'true' : ''}
      size={size}
      multiple={multiple}
      freeSolo={freeSolo}
      disabled={disabled}
      options={options}
      {...otherProps} // Includes value, onChange, getOptionLabel, etc.
      // RENDER FUNCTION: Custom TextField integration
      // This function is called by MUI Autocomplete to render the input field
      renderInput={(params) => (
        <TextField
          name={otherProps?.name}
          {...params} // MUI provides input props, refs, and event handlers
          // VISUAL PROPS: Pass through styling configuration
          color={color}
          variant={variant}
          alt={alt}
          // CONTENT PROPS: Field labeling and help
          label={label}
          placeholder={placeholder}
          helperText={helperText}
          // STATE PROPS: Validation and interaction state
          required={required}
          disabled={disabled}
          error={error}
          hiddenLabel={hiddenLabel}
          InputLabelProps={InputLabelProps}
        />
      )}
      ref={ref}
    />
  );
});

// Set display name for debugging and React DevTools
Autocomplete.displayName = 'Autocomplete';

// Comprehensive prop validation for development safety
Autocomplete.propTypes = {
  // STYLING & LAYOUT PROPS
  color: PropTypes.oneOf(['primary', 'secondary', 'info', 'success', 'warning', 'error']),
  variant: PropTypes.oneOf(['', 'outlined', 'filled', 'standard']),
  alt: PropTypes.bool /** Alternative layout mode for different design systems */,
  size: PropTypes.oneOf(['small', 'medium']),

  // CONTENT & BEHAVIOR PROPS
  label: PropTypes.string /** Field label text displayed above/within input */,
  placeholder: PropTypes.string /** Hint text shown when no value is selected */,
  value: PropTypes.any /** Current selected value(s) - controlled component */,
  defaultValue: PropTypes.any /** Initial value for uncontrolled usage */,
  options: PropTypes.array.isRequired /** Array of selectable options */,
  multiple: PropTypes.bool /** Enable multi-selection with chip display */,
  freeSolo: PropTypes.bool /** Allow custom text input beyond provided options */,

  // STATE & VALIDATION PROPS
  helperText: PropTypes.string /** Explanatory text displayed below the field */,
  required: PropTypes.bool /** Mark field as required (adds asterisk to label) */,
  disabled: PropTypes.bool /** Disable all user interactions */,
  error: PropTypes.bool /** Apply error styling (red colors) */,
  children: PropTypes.node /** Additional content (rarely used in Autocomplete) */,

  // ADVANCED CUSTOMIZATION PROPS
  hiddenLabel:
    PropTypes.bool /** Hide the label completely. Note: Will not work properly if 'label' prop is provided. */,
  InputLabelProps: PropTypes.object /** Props passed directly to the InputLabel component */,
  onChange:
    PropTypes.func /** Callback fired when selection changes: (event, value, reason, details?) => void */,
  getOptionLabel: PropTypes.func /** Function to extract display text from option objects */,
  isOptionEqualToValue: PropTypes.func /** Function to determine if option matches current value */,
  filterOptions: PropTypes.func /** Custom filtering logic for option search */,
};

export default Autocomplete;

// Display name for React DevTools debugging
Autocomplete.displayName = 'ERA² Autocomplete';

// REACT VERSION COMPATIBILITY NOTES:
// ==================================
//
// CURRENT APPROACH (React 18 Style):
// - Uses React.forwardRef for explicit ref forwarding
// - Compatible with React 16.3+ through React 18
// - Clear, debuggable ref handling
// - Consistent with existing codebase patterns
//
// FUTURE MIGRATION (React 19):
// When migrating to React 19, this component can be simplified to:
//
// const DebouncedTextField = ({ ref, maxLength = 0, debounceDelay = 500, ... }) => {
//   // Same implementation, but ref is just another prop
//   return <TextField ref={ref} ... />;
// };
//
// MIGRATION CHECKLIST:
// 1. Ensure all consuming components work with React 19
// 2. Remove React.forwardRef wrapper
// 3. Add ref to destructured props
// 4. Update PropTypes to include ref
// 5. Test ref forwarding functionality
// 6. Update documentation

// USAGE GUIDELINES:
// =================
//
// 1. OPTION FORMATS:
//    - Simple strings: ['Option 1', 'Option 2', 'Option 3']
//    - Objects: [{label: 'Display Text', value: 'internal_value'}, ...]
//    - Use getOptionLabel for object options: getOptionLabel={(option) => option.label}
//
// 2. PERFORMANCE CONSIDERATIONS:
//    - For large option lists (>100 items), consider virtualization
//    - Use filterOptions prop for custom search logic
//    - Debounce external API calls when using freeSolo mode
//
// 3. ACCESSIBILITY:
//    - Always provide meaningful labels
//    - Use helperText for additional context
//    - Consider required prop for form validation
//
// 4. MULTI-SELECT BEST PRACTICES:
//    - Limit to reasonable number of selections for UX
//    - Consider alternative UI for many options (e.g., separate multi-select component)
//    - Test chip overflow behavior on small screens
